import { Globe2, LocateFixed } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { baseMapImage, coverageMarkets, regionalDeliveryModel } from "@/data/eventOrganizerContent";

const mapPointToPercent = (lat, lng) => ({
  left: `${((lng + 180) / 360) * 100}%`,
  top: `${((90 - lat) / 180) * 100}%`,
});

const RegionalCoverageSection = () => {
  return (
    <section id="regional-coverage" data-testid="regional-coverage-section" className="space-y-7 py-16">
      <div className="max-w-5xl space-y-3">
        <p
          data-testid="regional-coverage-eyebrow-text"
          className="inline-flex items-center gap-2 rounded-full border border-fuchsia-200 bg-fuchsia-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-fuchsia-700"
        >
          <Globe2 className="h-3.5 w-3.5" />
          Pan-Asia Coverage
        </p>
        <h2 data-testid="regional-coverage-heading" className="text-3xl leading-tight text-violet-950 md:text-5xl">
          Regional coverage map and delivery model for multi-market corporate events.
        </h2>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
        <Card data-testid="regional-map-card" className="border-fuchsia-200 bg-violet-50/75">
          <CardContent className="space-y-4 p-5">
            <p data-testid="regional-map-label" className="text-sm font-semibold uppercase tracking-[0.12em] text-fuchsia-700">
              Coverage Map
            </p>

            <div
              data-testid="regional-map-canvas"
              className="relative overflow-hidden rounded-2xl border border-fuchsia-200 bg-[radial-gradient(circle_at_20%_20%,#f5d0fe_0%,#ede9fe_40%,#f8f6fb_100%)] p-4"
            >
              <div className="relative h-[290px] rounded-xl border border-fuchsia-200/80 bg-fuchsia-50/45">
                <img
                  src={baseMapImage}
                  alt="World base map with geographic coordinates"
                  data-testid="regional-map-geography"
                  className="absolute inset-0 h-full w-full object-cover object-center opacity-95"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-violet-950/18 via-transparent to-fuchsia-700/10" />

                {coverageMarkets.map((market, index) => (
                  <div
                    key={market.name}
                    className="absolute"
                    style={mapPointToPercent(market.lat, market.lng)}
                    data-testid={`regional-map-point-${index + 1}`}
                  >
                    {(() => {
                      const labelPositions = {
                        Tokyo: { left: "12px", top: "-14px" },
                        "Hong Kong": { left: "12px", top: "12px" },
                        Singapore: { left: "12px", top: "26px" },
                        Mumbai: { left: "-4px", top: "12px" },
                        Bangalore: { left: "6px", top: "26px" },
                        Sydney: { left: "-60px", top: "6px" },
                        Melbourne: { left: "-74px", top: "28px" },
                      };
                      const labelPosition = labelPositions[market.name] || { left: "10px", top: "-10px" };

                      return (
                        <>
                          <div className="coverage-dot relative -translate-x-1/2 -translate-y-1/2 rounded-full bg-fuchsia-600 p-1.5 text-white shadow-lg">
                            <LocateFixed className="h-3 w-3" />
                          </div>
                          <div
                            className="absolute rounded-md border border-fuchsia-200 bg-fuchsia-100/95 px-2 py-1 text-[11px] font-semibold text-violet-900 shadow-sm"
                            style={labelPosition}
                          >
                            {market.name}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
            <p data-testid="regional-map-accuracy-note" className="text-xs text-violet-800/80">
              Pins are plotted using geographic coordinates for city-level accuracy.
            </p>
          </CardContent>
        </Card>

        <Card data-testid="regional-delivery-model-card" className="border-fuchsia-200 bg-violet-50/75">
          <CardContent className="space-y-4 p-5">
            <p
              data-testid="regional-delivery-model-label"
              className="text-sm font-semibold uppercase tracking-[0.12em] text-fuchsia-700"
            >
              Regional Delivery Model
            </p>

            <div className="space-y-2.5">
              {regionalDeliveryModel.map((item, index) => (
                <div
                  key={item.stage}
                  data-testid={`regional-delivery-stage-${index + 1}`}
                  className="rounded-xl border border-fuchsia-200 bg-fuchsia-100/65 p-4"
                >
                  <p className="text-sm font-semibold uppercase tracking-[0.12em] text-violet-900">{item.stage}</p>
                  <p className="mt-1 text-sm leading-relaxed text-stone-700">{item.detail}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default RegionalCoverageSection;